import { useEffect, useState, type ReactNode } from "react";
import type { Installation as InstallationRow, PhotoEvidence } from "../api/types.js";
import { DeleteAction } from "../components/DeleteAction.jsx";
import { Async } from "../components/Async.jsx";
import { IconState } from "../components/Icons.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { coordinates, metres, standingOf, POSITION_WORD } from "../lib/evidence.js";
import { longDate, utcMoment } from "../lib/format.js";

/**
 * One installation: its photographs, and what each one recorded.
 *
 * WHY THIS IS NOT THE JOB'S EVIDENCE SCREEN, which it briefly was. Surveys and
 * installations are separate sections of this portal, and a row here used to
 * link straight into `#/jobs/:id/photos` — the PAS 2030 §6.11 record, which
 * belongs to the survey side. Clicking an installation therefore dumped you in
 * a different section with a different sidebar item highlighted and no way back
 * that did not go through the pipeline. Two sections that hand off to each
 * other are one section with extra steps.
 *
 * So this screen stays inside Installations. It reads the same photographs
 * through the same API — they ARE the same photographs, and a second copy of
 * the data would be the real duplication — but it answers the installer's
 * question rather than the assessor's: what was photographed here, where was
 * the phone standing, and has it all arrived.
 *
 * IT ASKS FOR THE INSTALLER'S VISITS ONLY, which is the difference that matters
 * between the two screens. The §6.11 record is every photograph on the job and
 * must stay that way; this section is the field work. Reading the unfiltered
 * record here put the surveyor's September photographs under an installation —
 * twenty-five on screen beneath a board that counted three.
 *
 * THE ASSESSOR'S QUESTIONS STAY ON THE SURVEY SIDE. Integrity re-hashing,
 * position overrides and the §6.11 shot list are not repeated here. This screen
 * shows the position as it stands and names it plainly when it was set by hand
 * — what it must never do is let a hand-set coordinate read as a measured one,
 * which is why `standingOf` decides that wording rather than this file.
 */
export function Installation({ jobId }: { readonly jobId: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<{
    readonly row: InstallationRow | undefined;
    readonly photos: readonly PhotoEvidence[];
  }>(async () => {
    const [list, photos] = await Promise.all([
      client.installations(),
      client.jobPhotos(jobId, { visit: "installation" }),
    ]);
    return {
      row: list.installations.find((i) => i.jobId === jobId),
      photos,
    };
  }, [client, jobId]);

  return (
    <>
      <p className="small">
        <a href={href.installations()}>Installations</a>
      </p>

      <Async state={state} what="this installation" reload={reload}>
        {({ row, photos }) => (
          <Detail row={row} photos={photos} jobId={jobId} reload={reload} />
        )}
      </Async>
    </>
  );
}

function Detail({
  row,
  photos,
  reload,
}: {
  readonly row: InstallationRow | undefined;
  readonly photos: readonly PhotoEvidence[];
  readonly jobId: string;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = photos.find((p) => p.id === selectedId) ?? photos[0];

  return (
    <>
      <div className="page-header spread">
        <div>
          {/* The reference IS the property: an installer types the address into
              it on the phone. There is no separate address to show. */}
          <h1>{row?.reference ?? "This installation"}</h1>
          <p className="lede">
            {row?.installerName ?? "Nobody assigned"} &middot;{" "}
            {photos.length} {photos.length === 1 ? "photograph" : "photographs"}
          </p>
        </div>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
      </div>

      {photos.length === 0 ? (
        <div className="state state--empty">
          <span className="state__glyph">
            <IconState kind="empty" />
          </span>
          <p className="state__title">No installation photographs have arrived</p>
          <p className="state__body">
            Only photographs taken on an installation visit appear here. A
            surveyor's are on the survey's photographic record instead. They
            appear as the phone syncs them, and nothing on this side can tell
            you what a phone is still holding.
          </p>
        </div>
      ) : (
        <div className="installation">
          <ul className="installation__grid">
            {photos.map((photo) => (
              <li key={photo.id}>
                <button
                  type="button"
                  className={
                    photo.id === selected?.id
                      ? "installation__tile installation__tile--on"
                      : "installation__tile"
                  }
                  onClick={() => setSelectedId(photo.id)}
                >
                  <Thumbnail photoId={photo.id} />
                  <span className="installation__caption">
                    {photo.capturedDescription ?? photo.subject}
                  </span>
                  <span className="installation__when">
                    {utcMoment(photo.capturedAt)}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {selected ? (
            <>
              <Facts photo={selected} />
              <DeleteAction
                what="photograph"
                warning={<RetentionWarning photo={selected} />}
                onDelete={(reason) => client.deletePhoto(selected.id, reason)}
                onDone={reload}
              />
            </>
          ) : null}
        </div>
      )}
    </>
  );
}

/**
 * One thumbnail.
 *
 * The bytes come through the authenticated client and become an object URL,
 * which this component OWNS — revoked on unmount, because an object URL pins
 * its blob for the life of the document and an installation with forty
 * photographs would otherwise hold forty full JPEGs alive across every
 * navigation. That is `photoImageObjectUrl`'s stated contract, not a
 * precaution.
 */
function Thumbnail({ photoId }: { readonly photoId: string }): ReactNode {
  const { client } = usePortal();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    let made: string | null = null;
    client
      .photoImageObjectUrl(photoId)
      .then((objectUrl) => {
        if (!live) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        made = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => {
        // A thumbnail that will not load is not a reason to fail the page.
        // The facts below it are the record; the picture is the illustration.
      });
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [client, photoId]);

  return url ? (
    <img className="installation__image" src={url} alt="" />
  ) : (
    <span className="installation__image installation__image--loading" />
  );
}

/** What this photograph recorded, in the order somebody reads it. */
/**
 * What deleting this photograph costs, said before it happens.
 *
 * A clause 6.11 photograph carries a retention minimum -- six, ten or
 * twenty-five years by measure -- because for concealed work it is the only
 * surviving proof the work was done correctly. The owner may delete one
 * anyway; what they may not do is delete one without being told that.
 */
function RetentionWarning({ photo }: { readonly photo: PhotoEvidence }): ReactNode {
  if (photo.retainUntil === null) {
    return <>This is photographic evidence of the work. Deleting it cannot be undone after thirty days.</>;
  }
  const due = new Date(photo.retainUntil);
  if (due.getTime() <= Date.now()) {
    return <>Its retention period ended on {longDate(photo.retainUntil)}, so it is no longer owed as evidence.</>;
  }
  return (
    <>
      This is evidence and is due to be kept until {longDate(photo.retainUntil)}. If the
      work it shows was later covered up, this photograph may be the only proof it was
      done correctly.
    </>
  );
}

function Facts({ photo }: { readonly photo: PhotoEvidence }): ReactNode {
  const standing = standingOf(photo);
  const position = coordinates(photo.latitude, photo.longitude);
  const accuracy = photo.measured.accuracyM;
  const bearing = photo.measured.bearingDeg;
  const altitude = photo.measured.altitudeM;

  return (
    <div className="installation__facts">
      <h2>{photo.capturedDescription ?? photo.subject}</h2>
      <dl>
        {/* Approximate, and said so. The job's own reference is the property;
            this is only the nearest postal address to where the phone stood. */}
        {photo.capturedAddress ? (
          <Fact label="Near">{photo.capturedAddress}</Fact>
        ) : null}
        <Fact label="Position">{position ?? "Not recorded"}</Fact>
        {/* Never allowed to read as measured when it was typed. */}
        <Fact label="How">{POSITION_WORD[standing]}</Fact>
        {accuracy !== null ? (
          <Fact label="Accuracy">{metres(accuracy)}</Fact>
        ) : null}
        {bearing !== null ? (
          <Fact label="Bearing">
            {bearing.toFixed(1)}&deg;{" "}
            {photo.measured.bearingRef === "true" ? "true" : "magnetic"}
          </Fact>
        ) : null}
        {altitude !== null ? (
          <Fact label="Altitude">{metres(altitude)}</Fact>
        ) : null}
        <Fact label="Taken">{utcMoment(photo.capturedAt)}</Fact>
      </dl>
    </div>
  );
}

function Fact({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
